import React, { useEffect, useRef, useState } from 'react'
import { InterfaceRoutes } from '../../interfaces/interfaceRoutes'
import './edificio-corporativo.css'
import { getFgEdificioCorporativos } from '../../config/services/strapi/edificio-corporativo'
import ImageSkeleton from '../../components/Skeleton/ImageSkeleton'
import TextSkeleton from '../../components/Skeleton/TextSkeleton'

type CarouselImage = {
  src: string
  alt: string
}

const FORCED_STRONG_TEXTS = [
  'Luis Izquierdo y Antonia Lehmann',
  'Sala Gasco Arte Contemporáneo',
  'Centro de Extensión GascoEduca'
]

const escapeRegExp = (value: string) => {
  const specials = new Set(['.', '*', '+', '?', '^', '$', '{', '}', '(', ')', '|', '[', ']', '\\'])
  let result = ''

  for (const ch of value) {
    result += specials.has(ch) ? `\\${ch}` : ch
  }

  return result
}

const renderTextWithForcedStrong = (text: string, keyPrefix: string) => {
  if (!text) return null

  const pattern = new RegExp(`(${FORCED_STRONG_TEXTS.map(escapeRegExp).join('|')})`, 'gi')

  return text.split(pattern).filter(Boolean).map((part: string, index: number) => {
    const isForcedStrong = FORCED_STRONG_TEXTS.some((target) => target.toLowerCase() === part.toLowerCase())
    if (isForcedStrong) {
      return <strong key={`${keyPrefix}-s-${index}`}>{part}</strong>
    }

    if (part.includes('\n')) {
      const lines = part.split('\n')
      return lines.map((line, lineIndex) => (
        <React.Fragment key={`${keyPrefix}-l-${index}-${lineIndex}`}>
          {line}
          {lineIndex < lines.length - 1 ? <br /> : null}
        </React.Fragment>
      ))
    }

    return <React.Fragment key={`${keyPrefix}-t-${index}`}>{part}</React.Fragment>
  })
}

const getMediaUrl = (media: any) => {
  const base = process.env.REACT_APP_STRAPI_ENDPOINT || 'http://localhost:1337'
  const raw = media?.url || media?.data?.attributes?.url || media?.attributes?.url || ''
  if (!raw) return ''
  if (raw.startsWith('http')) return raw
  return `${base}${raw}`
}

const normalizeHeroImages = (hero: any): CarouselImage[] => {
  if (!hero) return []

  const toImage = (item: any, index: number): CarouselImage | null => {
    const src = getMediaUrl(item)
    if (!src) return null
    const alt = item?.alternativeText || item?.name || `Edificio Corporativo ${index + 1}`
    return { src, alt }
  }

  if (Array.isArray(hero)) {
    const images: CarouselImage[] = []
    hero.forEach((item: any, index: number) => {
      const parsed = toImage(item, index)
      if (parsed) images.push(parsed)
    })
    return images
  }

  if (Array.isArray(hero?.data)) {
    const images: CarouselImage[] = []
    hero.data.forEach((item: any, index: number) => {
      const parsed = toImage(item, index)
      if (parsed) images.push(parsed)
    })
    return images
  }

  const single = toImage(hero?.data || hero, 0)
  return single ? [single] : []
}

const renderEdificioHoyFromBlocks = (blocks: any) => {
  if (!blocks) return null

  const items: Array<{ label: string; value: string }> = []
  const paragraphs: string[] = []
  const knownLabels = ['Dirección', 'Construcción', 'Remodelación', 'Arquitectos', 'Espacios culturales']

  const getMatchingLabel = (line: string) => {
    const normalized = (line || '').trim().toLowerCase()
    return knownLabels.find((label) => normalized.startsWith(label.toLowerCase()))
  }

  const consumeLine = (line: string, pendingLabelRef: { value: string }) => {
    const text = (line || '').trim()
    if (!text) return

    if (pendingLabelRef.value) {
      items.push({ label: pendingLabelRef.value, value: text })
      pendingLabelRef.value = ''
      return
    }

    const matchedLabel = getMatchingLabel(text)
    if (matchedLabel) {
      const value = text.slice(matchedLabel.length).trim()
      items.push({ label: matchedLabel, value })
      return
    }

    paragraphs.push(text)
  }

  const parseChildren = (children: any[]) => {
    const pendingLabelRef = { value: '' }

    children.forEach((child: any) => {
      if (child?.bold) {
        pendingLabelRef.value = (child?.text || '').trim()
        return
      }

      const lines = (child?.text || '')
        .split('\n')
        .map((s: string) => s.trim())
        .filter(Boolean)

      lines.forEach((line: string) => consumeLine(line, pendingLabelRef))
    })
  }

  if (typeof blocks === 'string') {
    blocks
      .split('\n')
      .map((s: string) => s.trim())
      .filter(Boolean)
      .forEach((line: string) => consumeLine(line, { value: '' }))
  } else if (Array.isArray(blocks)) {
    blocks.forEach((block: any) => {
      const children = (block?.children || []).filter((c: any) => (c?.text || '').trim() !== 'Drag')
      parseChildren(children)
    })
  }

  const intro = paragraphs[0] || ''
  const quote = paragraphs.length > 1 ? (paragraphs.at(-1) || '') : ''
  const middleParagraphs = paragraphs.length > 2 ? paragraphs.slice(1, -1) : []

  return (
    <>
      {intro ? <p className="qs-col-intro">{intro}</p> : null}

      {items.length ? (
        <ul className="qs-specs">
          {items.map((it) => (
            <li key={`ec-spec-${(it.label || '').slice(0, 12)}-${(it.value || '').slice(0, 12)}`}>
              <span className="qs-key">{it.label}</span>
              <span className="qs-value">{it.value}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {middleParagraphs.map((text) => (
        <p className="qs-col-intro" key={`ec-middle-${text.slice(0, 16).split(/\s+/).join('-')}`}>
          {text}
        </p>
      ))}

      {quote ? <blockquote className="qs-quote">{quote}</blockquote> : null}
    </>
  )
}

const renderDetalleFromBlocks = (blocks: any) => {
  if (!blocks) return null

  const sections: Array<{ title: string; description: string }> = []

  const parseParagraph = (raw: string) => {
    const text = (raw || '').trim()
    if (!text) return

    const firstBreak = text.indexOf('\n')
    if (firstBreak === -1) {
      sections.push({ title: text, description: '' })
      return
    }

    const title = text.slice(0, firstBreak).trim()
    const description = text.slice(firstBreak + 1).trim()
    if (!title && !description) return
    sections.push({ title, description })
  }

  if (typeof blocks === 'string') {
    blocks
      .split(/\n\s*\n/g)
      .map((s: string) => s.trim())
      .filter(Boolean)
      .forEach((paragraph: string) => parseParagraph(paragraph))
  }

  if (Array.isArray(blocks)) {
    blocks.forEach((block: any) => {
      const text = (block?.children || [])
        .filter((c: any) => (c?.text || '').trim() !== 'Drag')
        .map((c: any) => c?.text || '')
        .join('')

      parseParagraph(text)
    })
  }

  return (
    <>
      {sections.map((section) => (
        <div className="qs-linea" key={`ec-linea-${section.title.slice(0, 20).split(/\s+/).join('-')}`}>
          <h3 className="qs-linea-titulo">{section.title}</h3>
          {section.description ? <p>{renderTextWithForcedStrong(section.description, `ec-detalle-${section.title}`)}</p> : null}
        </div>
      ))}
    </>
  )
}

const EdificioCorporativo = (_props: InterfaceRoutes) => {
  const [loading, setLoading] = useState(true)
  const [activeSlide, setActiveSlide] = useState(0)
  const [carouselImages, setCarouselImages] = useState<CarouselImage[]>([])
  const [descripcion, setDescripcion] = useState<any>(null)
  const [detalle, setDetalle] = useState<any>(null)
  const trackRef = useRef<HTMLDivElement | null>(null)
  const slideRefs = useRef<Array<HTMLDivElement | null>>([])

  const scrollToSlide = (index: number, behavior: ScrollBehavior = 'smooth') => {
    const track = trackRef.current
    const slide = slideRefs.current[index]

    if (!track || !slide) {
      return
    }

    // Scroll only the horizontal track to avoid vertical page jumps.
    track.scrollTo({ left: slide.offsetLeft, behavior })
  }

  const goToSlide = (nextIndex: number, behavior: ScrollBehavior = 'smooth') => {
    if (!carouselImages.length) return
    const normalized = (nextIndex + carouselImages.length) % carouselImages.length
    setActiveSlide(normalized)
    scrollToSlide(normalized, behavior)
  }

  useEffect(() => {
    const load = async () => {
      try {
        const response = await getFgEdificioCorporativos()
        const attrs = response?.attributes || response || {}

        setCarouselImages(normalizeHeroImages(attrs?.hero))
        setDescripcion(attrs?.descripcion || null)
        setDetalle(attrs?.detalle || null)
      } catch (error) {
        console.error('Error cargando Edificio Corporativo:', error)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  useEffect(() => {
    if (carouselImages.length <= 1) return

    const intervalId = globalThis.setInterval(() => {
      goToSlide(activeSlide + 1)
    }, 5000)

    return () => globalThis.clearInterval(intervalId)
  }, [activeSlide, carouselImages.length])

  useEffect(() => {
    const track = trackRef.current

    if (!track) {
      return
    }

    const onScroll = () => {
      const nearestIndex = slideRefs.current.reduce((closestIndex, slide, index) => {
        if (!slide) {
          return closestIndex
        }

        const closestSlide = slideRefs.current[closestIndex]

        if (!closestSlide) {
          return index
        }

        const distance = Math.abs(slide.offsetLeft - track.scrollLeft)
        const closestDistance = Math.abs(closestSlide.offsetLeft - track.scrollLeft)

        return distance < closestDistance ? index : closestIndex
      }, 0)

      setActiveSlide(nearestIndex)
    }

    track.addEventListener('scroll', onScroll, { passive: true })

    return () => track.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className="edificio-page">
      <div id="hero-carousel" aria-label="Carrusel Edificio Corporativo">
        {loading ? (
          <ImageSkeleton className="home-hero-skeleton" />
        ) : (
          <div
            ref={trackRef}
            className="hero-track"
            aria-label="Galería desplazable de imágenes"
          >
            {carouselImages.map((image, index) => (
              <div
                key={`${image.src}-${index}`}
                ref={(element) => {
                  slideRefs.current[index] = element
                }}
                className="hero-slide"
              >
                <img src={image.src} alt={image.alt} draggable={false} />
              </div>
            ))}
          </div>
        )}

        <button className="carousel-btn prev" type="button" aria-label="Anterior" onClick={() => goToSlide(activeSlide - 1)}>
          <svg className="flickity-button-icon" viewBox="0 0 100 100" aria-hidden="true">
            <path d="M64.3 10.7L58.6 5 13.6 50l45 45 5.7-5.7L24.9 50z" />
          </svg>
        </button>
        <button className="carousel-btn next" type="button" aria-label="Siguiente" onClick={() => goToSlide(activeSlide + 1)}>
          <svg className="flickity-button-icon" viewBox="0 0 100 100" aria-hidden="true">
            <path d="M35.7 89.3L41.4 95l45-45-45-45-5.7 5.7L75.1 50z" />
          </svg>
        </button>

      </div>
      {!loading && (
        <div className="carousel-dots flickity-page-dots z-9999" aria-label="Indicadores carrusel">
          {carouselImages.map((image, index) => (
            <button
              key={`${image.src}-dot-${index}`}
              className={`dot ${activeSlide === index ? 'is-selected' : ''}`}
              type="button"
              aria-label={`Ir a imagen ${index + 1}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      )}

      <div id="main-content">
        <div className="page-content qs-content">
          

          <div className="qs-header">
            <h1 className="qs-title">Edificio Corporativo</h1>
          </div>

          <div className="qs-columns">
            <div className="qs-col">
              <h2 className="qs-col-title">EL EDIFICIO HOY</h2>
              {loading ? <TextSkeleton /> : renderEdificioHoyFromBlocks(descripcion)}
            </div>

            <div className="qs-col">
              <h2 className="qs-col-title">HISTORIA Y ARQUITECTURA</h2>
              {loading ? <TextSkeleton /> : renderDetalleFromBlocks(detalle)}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EdificioCorporativo
